import { Printer, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BASICS = [
  "Pick the same label size here as the labels loaded in your printer.",
  "In the print window, choose your label printer, not your office printer.",
  "Set Margins to None and Scale to 100% (or \"Actual size\"). Don't use \"Fit to page\".",
  "Turn off Headers and footers so the date and web address don't print on the label.",
  "Print one label first to check it, then print the rest.",
];

const PRINTERS = [
  {
    id: "brother-ql", name: "Brother QL (e.g. QL-800, QL-820NWB, QL-1110NWB)", size: "Standard 62 × 40mm or Large 100 × 50mm",
    steps: [
      "Load a 62mm continuous roll (e.g. DK-22205) for Standard labels, or a 102mm roll (e.g. DK-22243) for Large.",
      "Computer: install the Brother driver from support.brother.com. In Printing Preferences, add a custom paper size of 62 × 40mm (or 100 × 50mm), then choose it in the print window.",
      "iPhone/iPad: the Wi-Fi models (like QL-820NWB) support AirPrint. Tap Print, choose the printer and set Paper Size to your label size.",
      "Android: install \"Brother Print Service Plugin\" from Google Play, then print as normal.",
      "Labels feeding wrong or blank gaps: in the Brother settings tool, check the roll type matches the loaded roll.",
    ],
  },
  {
    id: "zebra", name: "Zebra desktop (e.g. ZD411, ZD421, GK420d)", size: "Small 50 × 25mm or Large 100 × 50mm",
    steps: [
      "Load the labels with the gap sensor set for die-cut labels. Hold the feed button to calibrate until it stops on the gap.",
      "Computer: install the ZDesigner driver from zebra.com. In Printing Preferences, set the label width and height (e.g. 50 × 25mm) and print darkness.",
      "Direct thermal models need direct thermal labels. Thermal transfer models also need a ribbon.",
      "Phones and tablets: Zebra printers usually print from a computer. For Android, Zebra's \"Print Connect\" app can help; check your model supports it.",
      "Text faint or patchy: raise Darkness one or two steps in Printing Preferences.",
    ],
  },
  {
    id: "dymo", name: "DYMO LabelWriter (e.g. 550, 5XL)", size: "Small 50 × 25mm or Large 100 × 50mm",
    steps: [
      "Use DYMO labels close to your chosen size: about 54 × 25mm for Small, or a 4-inch-wide (about 102mm) roll on the 5XL for Large.",
      "Install DYMO Connect from dymo.com, then print from the browser and choose the label size in the print window.",
      "If the label prints off-centre, pick the closest DYMO paper size and keep Scale at 100%.",
    ],
  },
];

export function PrinterGuide({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="printer-guide" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Printer className="h-5 w-5" />Setting up your label printer</DialogTitle></DialogHeader>
        <div className="rounded-2xl bg-emerald-50 p-4">
          <div className="ka-eyebrow mb-2 text-emerald-800">Every printer: 5 quick settings</div>
          <ol className="space-y-1.5 text-sm text-emerald-950">{BASICS.map((b, i) => <li key={b} className="flex gap-2"><span className="font-mono font-bold">{i + 1}.</span>{b}</li>)}</ol>
        </div>
        <div className="space-y-5">
          {PRINTERS.map((p) => (
            <section key={p.id} data-testid={`printer-guide-${p.id}`}>
              <h3 className="font-display text-base font-bold text-slate-900">{p.name}</h3>
              <div className="mb-2 text-xs font-semibold text-slate-500">Best size: {p.size}</div>
              <ul className="space-y-1.5 text-sm text-slate-700">{p.steps.map((s) => <li key={s} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{s}</li>)}</ul>
            </section>
          ))}
        </div>
        <p className="text-xs text-slate-500">Model names and roll codes are examples. Check your printer's manual for the exact rolls it takes.</p>
      </DialogContent>
    </Dialog>
  );
}
